import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Input, Modal, SpinnerOverlay } from '@components/common';
import TwoFaPhoneContainer from '@components/routes/UserProfile/TwoFaPhoneContainer';

import { useMutateCheckPhoneNumber } from '@hooks/userProfile/useMutateCheckPhoneNumber';

import { IProfileUserFormValues } from '@interfaces/user/userProfile';
import { PhoneNumber } from 'libphonenumber-js/types';

import { EUserProfileFormValue } from '@enums/userProfile';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480 } from '@constants/modals';

import { schema } from './schema';

import styles from './PhoneNumberForm.module.scss';

interface IPhoneNumberFormProps {
	prevPhoneNumber: PhoneNumber;
	onClose: () => void;
	codeTitle?: string;
	title?: string;
}

const PhoneNumberForm = ({
	prevPhoneNumber,
	onClose,
	title,
	codeTitle,
}: IPhoneNumberFormProps) => {
	const [isSendCodeForm, setIsSendCodeForm] = useState(false);

	const {
		checkPhoneNumber,
		isLoadingCheckPhoneNumber,
	} = useMutateCheckPhoneNumber({ onOpen: () => setIsSendCodeForm(true) });

	const {
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
		trigger,
	} = useForm<Pick<IProfileUserFormValues, typeof EUserProfileFormValue.phoneNumber>>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			[EUserProfileFormValue.phoneNumber]: prevPhoneNumber || '',
		},
	});

	const phoneNumberValue = watch(EUserProfileFormValue.phoneNumber);
	const phoneNumber: string = (phoneNumberValue ? `+${phoneNumberValue}` : '');

	const onChangePhoneNumber = (phone: string | PhoneNumber) => {
		const formattedPhone = String(phone).slice(1);

		setValue(EUserProfileFormValue.phoneNumber, formattedPhone as unknown as PhoneNumber);

		void trigger(EUserProfileFormValue.phoneNumber);
	};

	const onSubmit = async (
		values: Pick<IProfileUserFormValues,
			typeof EUserProfileFormValue.phoneNumber
		>) => {
		checkPhoneNumber({ number: values.phoneNumber });
	};

	return (
		<div className={styles.phoneNumberFormContainer}>
			{isLoadingCheckPhoneNumber && <SpinnerOverlay />}
			{
				isSendCodeForm
					? <Modal
						isOpen={isSendCodeForm}
						onClose={onClose}
						title={codeTitle || title}
						width={MODAL_WIDTH_480}
					>
						<TwoFaPhoneContainer
							onClose={onClose}
							phoneNumber={phoneNumber as unknown as PhoneNumber}
						/>
					</Modal>
					: <>
						<Input
							errorMessage={errors?.phoneNumber?.message}
							isPhoneNumber
							label='Contact Number'
							name={EUserProfileFormValue.phoneNumber}
							onChange={onChangePhoneNumber}
							placeholder='Enter contact number'
							required
							value={phoneNumber}
						/>
						<div
							className={styles.submitButtons}
						>
							<Button
								onClick={onClose}
								text='Cancel'
								withoutBackground
							/>
							<Button
								onClick={handleSubmit(onSubmit)}
								text='Next'
							/>
						</div>
					</>
			}
		</div>
	);
};

export default PhoneNumberForm;
